const GAME_CANVAS_WIDTH = 1200
const GAME_CANVAS_HEIGHT = 600
const STAGE_SCALE_X = 1
const STAGE_SCALE_Y = 1

const characterArchetypes = {
  brawler: { label: 'Brawler', role: 'Close range', description: 'Heavy impact, sturdy defense.', stats: { power: 9, speed: 5, jump: 4, defense: 7 }, specialName: 'Impact Breaker', maxJumps: 1 },
  ranger: { label: 'Ranger', role: 'Mid range', description: 'Fast angles and clean spacing.', stats: { power: 6, speed: 8, jump: 6, defense: 4 }, specialName: 'Vector Shot', maxJumps: 2 },
  striker: { label: 'Striker', role: 'Balanced', description: 'A flexible all-rounder build.', stats: { power: 7, speed: 7, jump: 5, defense: 5 }, specialName: 'Flux Dash', maxJumps: 2 },
  tank: { label: 'Tank', role: 'Front line', description: 'Slow, durable, and hard to move.', stats: { power: 8, speed: 3, jump: 4, defense: 9 }, specialName: 'Wall Crash', maxJumps: 1 },
}

export function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value))
}

export function buildPlayableStage(stagePreset) {
  if (!stagePreset) {
    console.error('buildPlayableStage: stagePreset is undefined')
    // Return a default stage
    return {
      id: 'fallback',
      name: 'Fallback Stage',
      left: 220,
      right: 980,
      floor: 450,
      ceiling: 0,
      blastBottom: 700,
      spawnLeft: 400,
      spawnRight: 800,
      spawnY: 350,
      platforms: [],
    }
  }
  
  return {
    ...stagePreset,
    platforms: (stagePreset.platforms || []).map((platform) => ({
      ...platform,
    })),
  }
}

export function makeFighter(character, x, stage, slot) {
  const speed = 3.2 + character.stats.speed * 0.26
  return {
    id: character.id,
    slot,
    name: character.name,
    x,
    y: stage.spawnY,
    spawnX: x,
    spawnY: stage.spawnY,
    vx: 0,
    vy: 0,
    width: 48,
    height: 64,
    facing: slot === 'p1' ? 1 : -1,
    onGround: false,
    hp: 0,
    stocks: 3,
    jumpCount: 0,
    maxJumps: character.maxJumps ?? characterArchetypes[character.archetype]?.maxJumps ?? 1,
    bodyColor: character.bodyColor || '#7cdbff',
    accentColor: character.accentColor || '#ff7bd6',
    stats: character.stats,
    specialName: character.specialName,
    speed,
    airSpeed: speed * 0.76,
    jumpPower: 650 + character.stats.jump * 24,
    dashSpeed: 7.5 + character.stats.speed * 0.55,
    attackCooldown: 0,
    dashCooldown: 0,
    dashTime: 0,
    invuln: 0,
    hitstun: 0,
    comboText: '',
    statusTimer: 0,
    prevX: x,
    prevY: stage.spawnY,
  }
}

export function updateFighterTimers(fighter, dt) {
  fighter.attackCooldown = Math.max(0, fighter.attackCooldown - dt)
  fighter.dashCooldown = Math.max(0, fighter.dashCooldown - dt)
  fighter.dashTime = Math.max(0, fighter.dashTime - dt)
  fighter.invuln = Math.max(0, fighter.invuln - dt)
  fighter.hitstun = Math.max(0, fighter.hitstun - dt)
  fighter.statusTimer = Math.max(0, fighter.statusTimer - dt)
}

export function applyControls(fighter, keys, isPressed, wasPressed, dt) {
  if (fighter.hitstun > 0) return

  const left = isPressed(keys.left)
  const right = isPressed(keys.right)
  const jump = wasPressed(keys.jump)
  const attack = isPressed(keys.attack)
  const special = isPressed(keys.special)
  const dash = wasPressed(keys.dash)

  const move = (right ? 1 : 0) - (left ? 1 : 0)
  const dashDirection = move !== 0 ? Math.sign(move) : fighter.facing
  const maxSpeed = fighter.onGround ? fighter.speed : fighter.airSpeed
  const accel = fighter.onGround ? 0.48 : 0.22

  if (dash && fighter.dashCooldown <= 0) {
    fighter.dashCooldown = fighter.onGround ? 0.45 : 0.55
    fighter.dashTime = 0.16
    fighter.comboText = 'Dash'
    fighter.statusTimer = 0.2
    fighter.vx = dashDirection * fighter.dashSpeed
    fighter.facing = dashDirection
    if (fighter.onGround) fighter.vy = Math.min(fighter.vy, 0)
  }

  if (move !== 0) {
    fighter.vx = clamp(fighter.vx + move * fighter.speed * accel, -maxSpeed, maxSpeed)
  } else if (fighter.onGround) {
    fighter.vx *= 0.78
  }

  if (jump && fighter.jumpCount < fighter.maxJumps) {
    fighter.vy = -(fighter.jumpPower + fighter.stats.jump * 8)
    fighter.onGround = false
    fighter.jumpCount += 1
    fighter.comboText = fighter.jumpCount > 1 ? 'Double Jump' : 'Jump'
    fighter.statusTimer = 0.5
  }

  if (attack && fighter.attackCooldown <= 0) {
    fighter.attackCooldown = 0.28
    fighter.comboText = 'Jab'
    fighter.statusTimer = 0.3
    fighter.vx += fighter.facing * (fighter.stats.power * 0.42 + 2.1)
  }

  if (special && fighter.attackCooldown <= 0) {
    fighter.attackCooldown = 0.62
    fighter.comboText = fighter.specialName
    fighter.statusTimer = 0.6
    fighter.vx += fighter.facing * (fighter.stats.power * 0.85 + 4.1)
    fighter.vy -= 140
  }

  if (left) fighter.facing = -1
  if (right) fighter.facing = 1
  if (!left && !right && !fighter.onGround) fighter.vx *= 0.99
  if (fighter.dashTime > 0) fighter.vx *= 1.015
  if (fighter.statusTimer <= 0 && fighter.attackCooldown <= 0) fighter.comboText = ''
}

export function updatePhysics(fighter, stage, dt) {
  fighter.prevX = fighter.x
  fighter.prevY = fighter.y
  fighter.vy += 1320 * dt
  fighter.x += fighter.vx * dt * 60
  fighter.y += fighter.vy * dt

  let landed = false
  // Only land on main floor if fighter's center is within the platform's horizontal bounds
  const fighterCenterX = fighter.x + fighter.width / 2
  const onMainPlatform = fighterCenterX >= stage.left && fighterCenterX <= stage.right
  if (onMainPlatform && fighter.y + fighter.height >= stage.floor) {
    fighter.y = stage.floor - fighter.height
    fighter.vy = 0
    fighter.onGround = true
    fighter.jumpCount = 0
  }

  for (const platform of stage.platforms) {
    const touchingTop = fighter.vy >= 0 && fighter.x + fighter.width > platform.x && fighter.x < platform.x + platform.width
    const crossedTop = fighter.prevY + fighter.height <= platform.y && fighter.y + fighter.height >= platform.y
    if (touchingTop && crossedTop) {
      fighter.y = platform.y - fighter.height
      fighter.vy = 0
      fighter.onGround = true
      fighter.jumpCount = 0
      landed = true
    }
  }

  if (!landed && fighter.y + fighter.height < stage.floor) {
    fighter.onGround = false
  }

  fighter.vx *= fighter.onGround ? 0.82 : 0.97
  // Allow fighters to walk past platform edge (into blast zone)
  // Only clamp to prevent going too far off screen
  fighter.x = clamp(fighter.x, stage.left - 200, stage.right + 200 - fighter.width)
  fighter.y = clamp(fighter.y, stage.ceiling - 120, stage.blastBottom + 100)
}

let aiTimer = 0

export function updateCpu(fighter, enemy, dt) {
  aiTimer -= dt
  const distance = (enemy.x + enemy.width / 2) - (fighter.x + fighter.width / 2)
  const heightGap = enemy.y - fighter.y
  fighter.facing = distance < 0 ? -1 : 1

  if (Math.abs(distance) > 132) {
    fighter.vx += Math.sign(distance) * fighter.speed * (fighter.onGround ? 0.26 : 0.18)
  }

  if (fighter.onGround && heightGap < -42 && fighter.jumpCount < fighter.maxJumps && aiTimer <= 0) {
    aiTimer = 0.55
    fighter.vy = -(fighter.jumpPower + 44)
    fighter.jumpCount += 1
    fighter.comboText = fighter.jumpCount > 1 ? 'CPU Double Jump' : 'CPU Jump'
    fighter.statusTimer = 0.45
  }

  if (Math.abs(distance) < 118 && fighter.attackCooldown <= 0 && aiTimer <= 0) {
    aiTimer = 0.45
    fighter.attackCooldown = 0.28
    fighter.comboText = 'CPU Combo'
    fighter.statusTimer = 0.3
    fighter.vx += fighter.facing * 3.8
  }

  if (!fighter.onGround) {
    fighter.vx += Math.sign(distance) * fighter.speed * 0.08
  }
}

export function resolveCombat(attacker, defender) {
  if (attacker.attackCooldown <= 0.2) return
  const ax = attacker.x + attacker.width / 2
  const ay = attacker.y + attacker.height / 2
  const dx = Math.abs(ax - (defender.x + defender.width / 2))
  const dy = Math.abs(ay - (defender.y + defender.height / 2))

  if (dx < 58 && dy < 42 && defender.invuln <= 0) {
    const damage = attacker.comboText === attacker.specialName ? 16 : 8
    defender.hp += damage
    defender.invuln = 0.45
    defender.hitstun = attacker.comboText === attacker.specialName ? 0.28 : 0.18
    defender.vx += attacker.facing * (4.5 + defender.hp * 0.06 + attacker.stats.power * 0.32)
    defender.vy -= 165 + defender.hp * 0.42
    defender.statusTimer = 0.22
  }
}

export function checkStocks(fighters, stage, onStockLost) {
  for (const fighter of fighters) {
    // Check if fighter fell off the bottom (always dies)
    const fellOffBottom = fighter.y > stage.blastBottom + 100
    
    // Side blast zones only apply if fighter is below the main platform floor level
    // This allows falling off side platforms without dying immediately
    const belowMainPlatform = fighter.y > stage.floor + 50
    const fellOffLeft = belowMainPlatform && fighter.x + fighter.width < stage.left - 100
    const fellOffRight = belowMainPlatform && fighter.x > stage.right + 100
    
    if (fellOffBottom || fellOffLeft || fellOffRight || fighter.hp >= 220) {
      fighter.stocks -= 1
      fighter.hp = 0
      fighter.x = fighter.spawnX
      fighter.y = fighter.spawnY
      fighter.vx = 0
      fighter.vy = 0
      fighter.hitstun = 0
      fighter.onGround = false
      fighter.jumpCount = 0
      fighter.invuln = 1.6
      fighter.comboText = 'Respawn'
      fighter.statusTimer = 0.75

      if (fighter.stocks <= 0) {
        return { gameOver: true, winner: fighters.find(f => f.stocks > 0) }
      }
    }
  }
  return { gameOver: false }
}

export function stageCameraBounds(stage) {
  const worldLeft = stage.left - 200
  const worldRight = stage.right + 200
  const worldTop = stage.ceiling - 220
  const worldBottom = stage.blastBottom + 120

  return {
    minX: worldLeft,
    maxX: Math.max(worldLeft, worldRight - GAME_CANVAS_WIDTH),
    minY: worldTop,
    maxY: Math.max(worldTop, worldBottom - GAME_CANVAS_HEIGHT),
  }
}

export function cameraTarget(fighters, stage) {
  // Fixed camera that shows the stage consistently
  return {
    x: (stage.left + stage.right) / 2 - GAME_CANVAS_WIDTH / 2,
    y: stage.floor - GAME_CANVAS_HEIGHT * 0.75,
  }
}

export function updateCamera(camera, fighters, stage, dt, immediate = false) {
  const bounds = stageCameraBounds(stage)
  const target = cameraTarget(fighters, stage)
  const nextX = clamp(target.x, bounds.minX, bounds.maxX)
  const nextY = clamp(target.y, bounds.minY, bounds.maxY)
  const smoothing = immediate ? 1 : Math.min(1, dt * 6)

  camera.x += (nextX - camera.x) * smoothing
  camera.y += (nextY - camera.y) * smoothing
}
